import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { API_LINK } from '../../apiLink/apiLink';

const AccountsPerformance = ({ onNavigate, userInfo }) => {

  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 1024);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // API Data States
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [accountsData, setAccountsData] = useState({
    name: "Loading...",
    accountsId: "...",
    department: "Accounts Department",
    designation: "Accounts Officer",
    profileImage: null
  });

  // Performance Data States
  const [performanceData, setPerformanceData] = useState({
    totalEmployees: 0,
    activeEmployees: 0,
    performanceMetrics: [],
    departmentStats: []
  });



  // Handle logout
  const handleLogout = () => {
    sessionStorage.clear();
    localStorage.clear();
    if (onNavigate) {
      onNavigate('home');
    } else {
      window.location.href = '/';
    }
  };

  // Fetch Performance Data from API
  useEffect(() => {
    const fetchPerformanceData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('🔍 Starting API call to fetch accounts performance data...');

        // Get token from storage
        const token = localStorage.getItem('token') || sessionStorage.getItem('token');
        console.log('🔑 Token found:', token ? 'Yes' : 'No');

        if (!token) {
          console.log('❌ No token found, using fallback data');
          setError('Authentication required. Please login again.');
          setAccountsData(prev => ({
            ...prev,
            name: userInfo?.name || "Accounts User",
            accountsId: userInfo?.id || "ACC2024001"
          }));
          setLoading(false);
          return;
        }

        // Construct the full API URL
        const apiUrl = `${API_LINK}/api/accounts/performance`;
        console.log('🌐 API URL:', apiUrl);

        const response = await fetch(apiUrl, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        console.log('📡 Response status:', response.status);
        console.log('📡 Response ok:', response.ok);

        if (!response.ok) {
          if (response.status === 401) {
            console.log('🔐 Authentication failed');
            handleLogout();
            return;
          } else if (response.status === 404) {
            console.log('🔍 API endpoint not found');
            setError('API endpoint not found.');
            setLoading(false);
            return;
          }
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          console.log('❌ Response is not JSON:', contentType);
          setError('Server returned non-JSON response.');
          setLoading(false);
          return;
        }

        const result = await response.json();
        console.log('📦 Full API Response:', result);

        if (result.success && result.data) {
          const data = result.data;
          console.log('✅ Processing performance data...');

          // Update accounts data
          setAccountsData({
            name: data.accounts?.name || userInfo?.name || "Accounts Officer",
            accountsId: data.accounts?.accountsId || userInfo?.id || "ACC2024001",
            department: data.accounts?.department || "Accounts Department",
            designation: data.accounts?.designation || "Accounts Officer",
            profileImage: data.accounts?.profileImage || null
          });

          // Update performance data
          setPerformanceData({
            totalEmployees: data.stats?.totalEmployees || 0,
            activeEmployees: data.stats?.activeEmployees || 0,
            performanceMetrics: data.performanceMetrics || [],
            departmentStats: data.departmentStats || []
          });

          console.log('✅ Performance data updated successfully');
        } else {
          console.log('❌ API returned error:', result.message);
          setError(result.message || 'Failed to fetch performance data');
        }

      } catch (error) {
        console.error('💥 Error fetching performance data:', error);
        setError(`Failed to load performance data: ${error.message}`);
      } finally {
        setLoading(false);
      }
    };

    const timer = setTimeout(fetchPerformanceData, 500);
    return () => clearTimeout(timer);
  }, [userInfo]);


  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };



  // Loading Component
  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{
          width: '50px',
          height: '50px',
          border: '4px solid #e3f2fd',
          borderTop: '4px solid #238548',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ color: '#666', fontSize: '1.1rem' }}>Loading Performance Management...</p>
        <style>
          {`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}
        </style>
      </div>
    );
  }

  // Error Component
  if (error) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        backgroundColor: '#f4f7fa',
        flexDirection: 'column',
        gap: '1rem'
      }}>
        <div style={{ fontSize: '3rem', color: '#f44336' }}>⚠️</div>
        <p style={{ color: '#666', fontSize: '1.1rem', textAlign: 'center', maxWidth: '400px' }}>
          {error}
        </p>
        <button
          onClick={() => window.location.reload()}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#238548',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <Layout role="accounts"
      onNavigate={onNavigate}
      activePage="accounts-performance"
      pageTitle="Performance Management"
      accountsData={accountsData}
    >
      {/* Page Header */}
      <div style={{
        background: '#ffffff',
        padding: '2rem',
        borderRadius: '12px',
        boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
        marginBottom: '2rem'
      }}>
        <h1 style={{
          margin: '0 0 1rem 0',
          color: '#333',
          fontSize: '2rem',
          fontWeight: 'bold'
        }}>
          📈 Performance Management
        </h1>
        <p style={{
          margin: 0,
          color: '#666',
          fontSize: '1.1rem',
          lineHeight: 1.6
        }}>
          Monitor and manage employee performance metrics, track productivity, and analyze departmental statistics.
        </p>
      </div>

      {/* Stats Cards */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: '1.5rem',
        marginBottom: '2rem'
      }}>
        {/* Total Employees Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>👥</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Total Employees</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {performanceData.totalEmployees > 0 ? performanceData.totalEmployees : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Active Staff
          </p>
        </div>

        {/* Active Employees Card */}
        <div style={{
          background: '#ffffff',
          padding: '2rem',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          textAlign: 'center',
          cursor: 'pointer',
          transition: 'transform 0.3s ease'
        }}>
          <div style={{ fontSize: '3rem', color: '#238548', marginBottom: '1rem' }}>✅</div>
          <h3 style={{ margin: '0 0 0.5rem 0', color: '#333', fontSize: '1.1rem' }}>Active Employees</h3>
          <p style={{
            fontSize: '2.2rem',
            fontWeight: 'bold',
            color: '#238548',
            margin: '0.5rem 0'
          }}>
            {performanceData.activeEmployees > 0 ? performanceData.activeEmployees : '0'}
          </p>
          <p style={{ fontSize: '1rem', color: '#666', margin: 0 }}>
            Currently Working
          </p>
        </div>
      </section>

      {/* Performance Metrics & Department Stats */}
      <section style={{
        display: 'grid',
        gridTemplateColumns: isMobile ? '1fr' : 'repeat(auto-fit, minmax(400px, 1fr))',
        gap: '1.5rem'
      }}>
        {/* Performance Metrics */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>📊</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Performance Metrics</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {performanceData.performanceMetrics.length > 0 ? performanceData.performanceMetrics.map((metric, index) => (
              <div key={index} style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '1rem',
                background: '#f8f9fa',
                borderRadius: '8px',
                marginBottom: '1rem',
                transition: 'all 0.3s ease'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold', color: '#333' }}>{metric.name}</div>
                  <div style={{ color: '#666', fontSize: '0.9rem' }}>{metric.description}</div>
                </div>
                <div style={{
                  padding: '0.5rem 1rem',
                  background: metric.status === 'excellent' ? '#e8f5e9' : metric.status === 'good' ? '#fff3cd' : '#ffebee',
                  color: metric.status === 'excellent' ? '#2e7d32' : metric.status === 'good' ? '#856404' : '#c62828',
                  borderRadius: '20px',
                  fontSize: '0.9rem',
                  fontWeight: 'bold'
                }}>
                  {metric.value}%
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No performance metrics available
              </p>
            )}
          </div>
        </div>

        {/* Department Statistics */}
        <div style={{
          background: '#ffffff',
          borderRadius: '12px',
          boxShadow: '0 4px 15px rgba(0,0,0,0.1)',
          overflow: 'hidden'
        }}>
          <div style={{
            background: '#238548',
            color: '#ffffff',
            padding: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            gap: '1rem'
          }}>
            <span style={{ fontSize: '1.8rem' }}>🏢</span>
            <h3 style={{ margin: 0, fontSize: '1.2rem' }}>Department Statistics</h3>
          </div>
          <div style={{ padding: '1.5rem' }}>
            {performanceData.departmentStats.length > 0 ? performanceData.departmentStats.map((dept, index) => (
              <div key={index} style={{
                padding: '1rem',
                borderBottom: index < performanceData.departmentStats.length - 1 ? '1px solid #ecf0f1' : 'none',
                transition: 'all 0.3s ease'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <div>
                    <div style={{ fontWeight: 'bold', color: '#333' }}>{dept.name}</div>
                    <div style={{ color: '#666', fontSize: '0.9rem' }}>
                      {dept.employeeCount} employees
                    </div>
                  </div>
                  <div style={{
                    padding: '0.25rem 0.75rem',
                    borderRadius: '15px',
                    fontSize: '0.8rem',
                    fontWeight: 500,
                    backgroundColor: dept.performance >= 90 ? '#e8f5e9' : dept.performance >= 70 ? '#fff3cd' : '#ffebee',
                    color: dept.performance >= 90 ? '#2e7d32' : dept.performance >= 70 ? '#856404' : '#c62828'
                  }}>
                    {dept.performance}% Performance
                  </div>
                </div>
              </div>
            )) : (
              <p style={{ textAlign: 'center', color: '#666', padding: '2rem' }}>
                No department statistics available
              </p>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default AccountsPerformance;
